import { get, post } from './client'

export interface PayMethod {
  name: string
  type: string
  color?: string
  min_topup?: string
}

export interface WaffoPayMethod {
  name: string
  icon?: string
  payMethodType: string
  payMethodName: string
}

export interface CreemProduct {
  name: string
  productId: string
  price: number
  quota: number
  currency?: string
}

export interface TopupInfo {
  enable_online_topup?: boolean
  enable_stripe_topup?: boolean
  enable_creem_topup?: boolean
  enable_waffo_topup?: boolean
  pay_methods?: PayMethod[]
  waffo_pay_methods?: WaffoPayMethod[]
  creem_products?: string
  min_topup?: number
  stripe_min_topup?: number
  waffo_min_topup?: number
  amount_options?: number[]
  discount?: Record<string, number>
}

export interface TopUpRecord {
  id: number
  user_id: number
  amount: number
  money: number
  trade_no: string
  payment_method: string
  create_time: number
  complete_time?: number
  status: string
}

export interface PaginatedTopUps {
  items: TopUpRecord[]
  total: number
  page?: number
  page_size?: number
}

interface LegacyPayResponse {
  message?: string
  data?: unknown
  url?: string
}

export type PaymentChannel = 'epay' | 'stripe' | 'creem' | 'waffo'

function throwPayError(res: LegacyPayResponse): never {
  const data = res.data
  const msg =
    typeof data === 'string'
      ? data
      : typeof res.message === 'string' && res.message !== 'success'
        ? res.message
        : '支付请求失败'
  throw new Error(msg)
}

export function parseLegacyPayResponse<T>(res: LegacyPayResponse): T {
  if (res.message !== 'success') {
    throwPayError(res)
  }
  return res.data as T
}

/** 易支付：动态 POST 表单跳转 */
export function submitEpayForm(url: string, params: Record<string, string>): void {
  const form = document.createElement('form')
  form.action = url
  form.method = 'POST'
  const isSafari =
    navigator.userAgent.indexOf('Safari') > -1 && navigator.userAgent.indexOf('Chrome') < 1
  if (!isSafari) {
    form.target = '_blank'
  }
  Object.entries(params).forEach(([key, value]) => {
    const input = document.createElement('input')
    input.type = 'hidden'
    input.name = key
    input.value = value
    form.appendChild(input)
  })
  document.body.appendChild(form)
  form.submit()
  document.body.removeChild(form)
}

export function parseCreemProducts(raw: string | undefined): CreemProduct[] {
  if (!raw || raw === '[]') return []
  try {
    const parsed = JSON.parse(raw) as CreemProduct[]
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

export function getTopupReturnUrls() {
  const origin = window.location.origin
  return {
    successUrl: `${origin}/console/topup?show_history=true`,
    cancelUrl: `${origin}/console/topup`,
  }
}

/**
 * 获取充值配置
 * GET /api/user/topup/info
 */
export async function getTopupInfoApi(): Promise<TopupInfo> {
  return get<TopupInfo>('/api/user/topup/info')
}

/**
 * 使用兑换码充值
 * POST /api/user/topup — 返回增加的 quota 整数
 */
export async function redeemCodeApi(key: string): Promise<number> {
  const data = await post<number>('/api/user/topup', { key })
  return typeof data === 'number' ? data : Number(data) || 0
}

/**
 * 易支付金额预估
 * POST /api/user/amount — 返回应付金额字符串
 */
export async function calculateEpayAmountApi(amount: number): Promise<string> {
  const res = (await post('/api/user/amount', { amount })) as LegacyPayResponse | string
  if (typeof res === 'string') return res
  return parseLegacyPayResponse<string>(res)
}

/**
 * Stripe 金额预估
 * POST /api/user/stripe/amount
 */
export async function calculateStripeAmountApi(amount: number): Promise<string> {
  const res = (await post('/api/user/stripe/amount', { amount })) as LegacyPayResponse | string
  if (typeof res === 'string') return res
  return parseLegacyPayResponse<string>(res)
}

/**
 * 发起易支付订单
 * POST /api/user/pay
 */
export async function createPayOrderApi(params: {
  amount: number
  payment_method: string
}): Promise<{ url: string; params: Record<string, string> }> {
  const res = (await post('/api/user/pay', params)) as LegacyPayResponse & { url?: string }
  if (typeof res !== 'object' || res === null || Array.isArray(res)) {
    throw new Error('支付响应格式错误')
  }
  if (res.message !== 'success') {
    throwPayError(res)
  }
  const formParams = (res.data || {}) as Record<string, string>
  if (!res.url) {
    throw new Error('未获取到支付跳转地址')
  }
  return { url: res.url, params: formParams }
}

/**
 * Stripe 充值
 * POST /api/user/stripe/pay
 */
export async function createStripeOrderApi(params: {
  amount: number
  success_url?: string
  cancel_url?: string
}): Promise<string> {
  const res = (await post('/api/user/stripe/pay', {
    amount: params.amount,
    payment_method: 'stripe',
    success_url: params.success_url,
    cancel_url: params.cancel_url,
  })) as LegacyPayResponse
  const data = parseLegacyPayResponse<{ pay_link?: string }>(res)
  if (!data.pay_link) {
    throw new Error('未获取到 Stripe 支付链接')
  }
  return data.pay_link
}

/**
 * Creem 充值
 * POST /api/user/creem/pay
 */
export async function createCreemOrderApi(productId: string): Promise<string> {
  const res = (await post('/api/user/creem/pay', {
    product_id: productId,
    payment_method: 'creem',
  })) as LegacyPayResponse
  const data = parseLegacyPayResponse<{ checkout_url?: string }>(res)
  if (!data.checkout_url) {
    throw new Error('未获取到 Creem 结账链接')
  }
  return data.checkout_url
}

/**
 * Waffo 充值
 * POST /api/user/waffo/pay
 */
export async function createWaffoOrderApi(params: {
  amount: number
  pay_method_index?: number
}): Promise<string> {
  const res = (await post('/api/user/waffo/pay', params)) as LegacyPayResponse
  const data = parseLegacyPayResponse<{ payment_url?: string }>(res)
  if (!data.payment_url) {
    throw new Error('未获取到 Waffo 支付链接')
  }
  return data.payment_url
}

export interface TopupListParams {
  p?: number
  page_size?: number
  keyword?: string
}

function normalizeTopupPage(data: PaginatedTopUps | TopUpRecord[]): PaginatedTopUps {
  if (Array.isArray(data)) {
    return { items: data, total: data.length }
  }
  return {
    items: data.items || [],
    total: data.total ?? 0,
    page: data.page,
    page_size: data.page_size,
  }
}

/**
 * 用户充值记录
 * GET /api/user/topup/self
 */
export async function getUserTopupsApi(params: TopupListParams = {}): Promise<PaginatedTopUps> {
  const data = await get<PaginatedTopUps | TopUpRecord[]>('/api/user/topup/self', params)
  return normalizeTopupPage(data)
}

/**
 * 管理员全站充值记录
 * GET /api/user/topup
 */
export async function getAllTopupsApi(params: TopupListParams = {}): Promise<PaginatedTopUps> {
  const data = await get<PaginatedTopUps | TopUpRecord[]>('/api/user/topup', params)
  return normalizeTopupPage(data)
}

/**
 * 管理员手动补单
 * POST /api/user/topup/complete
 */
export async function adminCompleteTopupApi(tradeNo: string): Promise<void> {
  await post('/api/user/topup/complete', { trade_no: tradeNo })
}
